/**
 * @file src/core/loaders/gltf.js
 * GLTFLoader dựng ĐÚNG như GLB.check/gltf-viewer: meshopt + Draco + KTX2 luôn có mặt.
 *
 * VÌ SAO BỎ CƠ CHẾ "chỉ bật Draco khi manifest khai":
 *   Bản cũ chỉ gọi setDRACOLoader() khi assets.manifest.json có `meta.compression:
 *   "draco"`. Model do giáo viên/đối tác xuất ra KHÔNG kèm khai báo đó, và khi thiếu thì
 *   bài TREO Ở 98% màn hình tải, console chỉ có "Unexpected token '<'" — đúng cái bẫy
 *   đã ghi trong header của tiết his-06. Cùng một file .glb mở trong GLB.check thì chạy,
 *   mở trong bài thì treo: sai lệch này làm mất tác dụng của khâu duyệt model.
 *
 * GIÁ PHẢI TRẢ gần như bằng 0: hai loader chỉ là ~40 KB JS, và wasm decoder KHÔNG được
 * tải cho tới khi thật sự gặp primitive Draco / texture KTX2 (không gọi preload()).
 * Model không nén thì không tốn thêm một byte mạng nào.
 */

import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

/** Decoder dùng chung cho mọi loader — nặng, không dựng lại mỗi lần. */
/** @type {any} */
let _dracoLoader = null;
/** @type {any} */
let _ktx2Loader = null;

export const DRACO_PATH = '/_core/vendor/draco/';
export const BASIS_PATH = '/_core/vendor/basis/';

/**
 * @param {{ manager?: import('three').LoadingManager,
 *           renderer?: import('three').WebGLRenderer,
 *           dracoPath?: string, ktx2Path?: string }} opts
 * @returns {Promise<GLTFLoader>}
 */
export async function createGLTFLoader(opts = {}) {
  const loader = new GLTFLoader(opts.manager);

  // meshopt: ~25 KB, là format nén mặc định của pipeline nội bộ
  loader.setMeshoptDecoder(MeshoptDecoder);

  await enableDraco(loader, opts.dracoPath);
  if (opts.renderer) await enableKTX2(loader, opts.renderer, opts.ktx2Path);

  return loader;
}

/**
 * Gắn DRACOLoader. Decoder wasm chỉ tải khi gặp primitive Draco (không preload).
 * @param {GLTFLoader} loader
 * @param {string} [decoderPath]
 */
export async function enableDraco(loader, decoderPath = DRACO_PATH) {
  if (!_dracoLoader) {
    const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
    _dracoLoader = new DRACOLoader();
    _dracoLoader.setDecoderPath(decoderPath);
  }
  loader.setDRACOLoader(_dracoLoader);
  return loader;
}

/**
 * Gắn KTX2Loader. detectSupport() chỉ đọc extension của GL context — rẻ; transcoder wasm
 * chỉ tải khi gặp texture .ktx2.
 * @param {GLTFLoader} loader
 * @param {import('three').WebGLRenderer} renderer
 * @param {string} [transcoderPath]
 */
export async function enableKTX2(loader, renderer, transcoderPath = BASIS_PATH) {
  if (!_ktx2Loader) {
    const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
    _ktx2Loader = new KTX2Loader();
    _ktx2Loader.setTranscoderPath(transcoderPath);
    _ktx2Loader.detectSupport(renderer);
  }
  loader.setKTX2Loader(_ktx2Loader);
  return loader;
}

/** Dọn decoder dùng chung. tools/smoke gọi giữa các lần chạy. */
export function disposeSharedDecoders() {
  _dracoLoader?.dispose?.();
  _dracoLoader = null;
  _ktx2Loader?.dispose?.();
  _ktx2Loader = null;
}
